import React, { useEffect, useLayoutEffect, useState } from "react";
import { Button } from "./ui/button";

/**
 * Visite guidée légère : met en surbrillance des éléments ciblés par data-testid.
 * steps: [{ selector, title, text }]
 */
export const GuidedTour = ({ steps = [], run, onFinish, onSkip, onStepChange }) => {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);

  const current = steps[index];

  useEffect(() => {
    if (run && current) onStepChange?.(current);
  }, [index, run]); // eslint-disable-line

  const measure = () => {
    if (!current) return;
    const el = document.querySelector(current.selector);
    if (el) {
      el.scrollIntoView({ block: "nearest", inline: "center" });
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setRect(null);
    }
  };

  useLayoutEffect(() => {
    if (run) measure();
  }, [index, run]); // eslint-disable-line

  useEffect(() => {
    if (!run) return;
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [run, index]); // eslint-disable-line

  useEffect(() => { if (run) setIndex(0); }, [run]);

  if (!run || !current) return null;

  const isLast = index === steps.length - 1;
  const pad = 6;

  // Position de la carte info : sous la cible si possible, sinon centrée
  const cardTop = rect ? Math.min(rect.top + rect.height + 12, window.innerHeight - 200) : window.innerHeight / 2 - 100;
  const cardLeft = rect ? Math.max(12, Math.min(rect.left, window.innerWidth - 340)) : window.innerWidth / 2 - 170;

  const next = () => (isLast ? onFinish?.() : setIndex((i) => i + 1));
  const prev = () => setIndex((i) => Math.max(0, i - 1));

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="guided-tour">
      {/* Voile */}
      <div className="absolute inset-0 bg-black/50" onClick={onSkip} />

      {/* Surbrillance de la cible */}
      {rect && (
        <div
          className="absolute rounded-lg ring-4 ring-orange-500 pointer-events-none transition-all duration-200"
          style={{
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.5)",
          }}
        />
      )}

      {/* Carte d'explication */}
      <div
        className="absolute w-[320px] bg-white rounded-lg shadow-xl p-4 border"
        style={{ top: cardTop, left: cardLeft }}
        data-testid="guided-tour-card"
      >
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-orange-600 font-medium">Étape {index + 1} / {steps.length}</span>
          <button
            onClick={onSkip}
            className="text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-2.5 py-1 rounded-full transition-colors"
            data-testid="tour-skip"
          >
            Passer la visite ✕
          </button>
        </div>
        <h3 className="font-semibold text-gray-800 mb-1">{current.title}</h3>
        <p className="text-sm text-gray-600 mb-3">{current.text}</p>
        <div className="flex items-center justify-between gap-2">
          <button onClick={onSkip} className="text-xs text-gray-400 hover:text-gray-600 underline" data-testid="tour-skip-link">
            Ne plus afficher
          </button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={prev} disabled={index === 0}>Précédent</Button>
            <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={next} data-testid="tour-next">
              {isLast ? "Terminer" : "Suivant"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedTour;
